import { __awaiter } from "tslib";
import { makeAutoObservable, runInAction } from "mobx";
import { kAnalyzerUserParams, kExemplarUserParams } from "../../shared/shared";
const formatStudentName = (fullName, showLastNameFirst) => {
    if (showLastNameFirst) {
        const parts = fullName.split(" ");
        if (parts.length >= 2) {
            const lastName = parts[parts.length - 1];
            const firstName = parts.slice(0, -1).join(" ");
            return `${lastName}, ${firstName}`;
        }
    }
    return fullName;
};
export class GroupManagementState {
    constructor(groups, classStore, user, db, mode) {
        Object.defineProperty(this, "groups", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "classStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "user", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "db", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "mode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pendingMoves", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        Object.defineProperty(this, "selectedStudentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "selectedGroupId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "createdGroupsInSession", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "showLastNameFirst", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isSaving", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "savingFailed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "draggingStudentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "draggingStudentName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "draggingStudentConnected", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        makeAutoObservable(this);
        this.groups = groups;
        this.classStore = classStore;
        this.user = user;
        this.db = db;
        this.mode = mode;
    }
    get isTeacherMode() {
        return this.mode === "teacher" || this.mode === "researcher";
    }
    get allStudents() {
        // Exclude virtual users Ivan Idea and Ada Insight. They are added to the class roster for exemplar/AI
        // features but shouldn't appear as groupable students.
        return this.classStore.students.filter(student => student.id !== kExemplarUserParams.id && student.id !== kAnalyzerUserParams.id);
    }
    get existingGroupIds() {
        return new Set(this.groups.allGroups.map(g => g.id));
    }
    get studentGroupMap() {
        const map = new Map();
        this.allStudents.forEach(student => {
            const group = this.groups.groupForUser(student.id);
            map.set(student.id, (group === null || group === void 0 ? void 0 : group.id) || null);
        });
        if (this.isTeacherMode) {
            this.pendingMoves.forEach((toGroupId, studentId) => {
                map.set(studentId, toGroupId);
            });
        }
        if (this.mode === "student" && this.selectedGroupId !== null) {
            map.set(this.user.id, this.selectedGroupId);
        }
        return map;
    }
    get newGroupIds() {
        const ids = [];
        if (this.isTeacherMode) {
            this.pendingMoves.forEach((toGroupId) => {
                if (toGroupId !== null && !this.existingGroupIds.has(toGroupId)) {
                    if (!ids.includes(toGroupId)) {
                        ids.push(toGroupId);
                    }
                }
            });
        }
        if (this.mode === "student" && this.selectedGroupId !== null && !this.existingGroupIds.has(this.selectedGroupId)) {
            if (!ids.includes(this.selectedGroupId)) {
                ids.push(this.selectedGroupId);
            }
        }
        // Include all groups created in this session, including empty groups.
        this.createdGroupsInSession.forEach(groupId => {
            if (!this.existingGroupIds.has(groupId) && !ids.includes(groupId)) {
                ids.push(groupId);
            }
        });
        return ids.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    }
    // Determines the next available group ID for "New Group".
    get nextGroupId() {
        const allUsedIds = new Set([...this.existingGroupIds, ...this.newGroupIds]);
        const existingNumericIds = [...allUsedIds]
            .map(id => parseInt(id, 10))
            .filter(n => !isNaN(n));
        const maxId = existingNumericIds.length > 0 ? Math.max(...existingNumericIds) : 0;
        return String(maxId + 1);
    }
    get currentUserGroupId() {
        var _a;
        return ((_a = this.groups.groupForUser(this.user.id)) === null || _a === void 0 ? void 0 : _a.id) || null;
    }
    // First-time join: student mode where the current user has no group yet
    get isFirstTimeJoin() {
        return this.mode === "student" && this.currentUserGroupId === null;
    }
    // In student mode, the current user is always "selected" for moving
    get effectiveSelectedStudentId() {
        return this.mode === "student" ? this.user.id : this.selectedStudentId;
    }
    get hasChanges() {
        if (this.isTeacherMode) {
            return this.pendingMoves.size > 0;
        }
        return this.selectedGroupId !== null && this.selectedGroupId !== this.currentUserGroupId;
    }
    get modalTitle() {
        if (this.isTeacherMode) {
            return "Move Students to Different Groups";
        }
        return this.currentUserGroupId === null
            ? "Join Group"
            : "Join a Different Group";
    }
    get instructions() {
        if (this.isTeacherMode) {
            return "To move a student, select the student then select — or drag to — a different group:";
        }
        return this.currentUserGroupId === null
            ? "To join a group, select a group:"
            : "To join a different group, select a group or drag your name to a group:";
    }
    // Students not in any group
    get unassignedStudents() {
        return this.getStudentsForGroup(null);
    }
    reset() {
        this.pendingMoves = new Map();
        this.selectedStudentId = null;
        this.selectedGroupId = null;
        this.createdGroupsInSession = new Set();
        this.savingFailed = false;
        this.draggingStudentId = null;
        this.draggingStudentName = null;
        this.draggingStudentConnected = false;
    }
    selectStudent(studentId) {
        if (this.isTeacherMode) {
            this.selectedStudentId = this.selectedStudentId === studentId ? null : studentId;
        }
    }
    selectGroup(groupId) {
        if (this.isTeacherMode && this.selectedStudentId) {
            const currentGroupId = this.studentGroupMap.get(this.selectedStudentId);
            if (currentGroupId !== groupId) {
                if (!this.existingGroupIds.has(groupId)) {
                    this.createdGroupsInSession = new Set([...this.createdGroupsInSession, groupId]);
                }
                const newMoves = new Map(this.pendingMoves);
                newMoves.set(this.selectedStudentId, groupId);
                this.pendingMoves = newMoves;
            }
        }
        else if (this.mode === "student") {
            if (!this.existingGroupIds.has(groupId)) {
                this.createdGroupsInSession = new Set([...this.createdGroupsInSession, groupId]);
            }
            this.selectedGroupId = groupId;
        }
    }
    moveStudentToNoGroup() {
        if (this.isTeacherMode && this.selectedStudentId) {
            const newMoves = new Map(this.pendingMoves);
            newMoves.set(this.selectedStudentId, null);
            this.pendingMoves = newMoves;
        }
    }
    startDrag(studentId, studentName) {
        var _a;
        this.draggingStudentId = studentId;
        this.draggingStudentName = studentName;
        const studentGroup = this.groups.groupForUser(studentId);
        const groupUser = studentGroup === null || studentGroup === void 0 ? void 0 : studentGroup.getUserById(studentId);
        this.draggingStudentConnected = (_a = groupUser === null || groupUser === void 0 ? void 0 : groupUser.connected) !== null && _a !== void 0 ? _a : false;
        if (this.isTeacherMode) {
            this.selectedStudentId = studentId;
        }
    }
    endDrag(targetGroupId, isNoGroup = false) {
        const studentId = this.draggingStudentId;
        if (!studentId) {
            this.clearDragState();
            return;
        }
        const effectiveTargetGroupId = isNoGroup ? null : targetGroupId;
        if (this.isTeacherMode) {
            const currentGroupId = this.studentGroupMap.get(studentId);
            if (currentGroupId !== effectiveTargetGroupId) {
                if (effectiveTargetGroupId !== null && !this.existingGroupIds.has(effectiveTargetGroupId)) {
                    this.createdGroupsInSession = new Set([...this.createdGroupsInSession, effectiveTargetGroupId]);
                }
                const newMoves = new Map(this.pendingMoves);
                newMoves.set(studentId, effectiveTargetGroupId);
                this.pendingMoves = newMoves;
            }
        }
        else if (this.mode === "student" && studentId === this.user.id && effectiveTargetGroupId !== null) {
            if (!this.existingGroupIds.has(effectiveTargetGroupId)) {
                this.createdGroupsInSession = new Set([...this.createdGroupsInSession, effectiveTargetGroupId]);
            }
            this.selectedGroupId = effectiveTargetGroupId;
        }
        this.clearDragState();
    }
    clearDragState() {
        this.draggingStudentId = null;
        this.draggingStudentName = null;
        this.draggingStudentConnected = false;
    }
    setShowLastNameFirst(value) {
        this.showLastNameFirst = value;
    }
    handleGroupCardSelect(groupId, isNoGroup) {
        return __awaiter(this, void 0, void 0, function* () {
            if (isNoGroup) {
                this.moveStudentToNoGroup();
            }
            else {
                this.selectGroup(groupId);
            }
        });
    }
    save(onSave) {
        return __awaiter(this, void 0, void 0, function* () {
            runInAction(() => {
                this.isSaving = true;
                this.savingFailed = false;
            });
            try {
                // Create empty groups that were added in this session but don't have students in them.
                const emptyGroupsToCreate = [...this.createdGroupsInSession].filter(groupId => !this.existingGroupIds.has(groupId) &&
                    (!this.isTeacherMode || !Array.from(this.pendingMoves.values()).includes(groupId)) &&
                    (this.isTeacherMode || groupId !== this.selectedGroupId));
                if (emptyGroupsToCreate.length > 0) {
                    for (const groupId of emptyGroupsToCreate) {
                        yield this.db.createEmptyGroup(groupId);
                    }
                }
                if (this.isTeacherMode && onSave) {
                    yield onSave(this.pendingMoves);
                }
                else if (this.mode === "student" && this.selectedGroupId) {
                    yield this.db.moveStudentToGroup(this.user.id, this.selectedGroupId);
                }
                return true;
            }
            catch (error) {
                console.error("Error saving group changes:", error);
                runInAction(() => {
                    this.savingFailed = true;
                });
                return false;
            }
            finally {
                runInAction(() => {
                    this.isSaving = false;
                });
            }
        });
    }
    getStudentsForGroup(groupId) {
        return this.allStudents
            .filter(student => this.studentGroupMap.get(student.id) === groupId)
            .map(student => {
            var _a, _b;
            const studentGroup = this.groups.groupForUser(student.id);
            // The current user is always connected since they're actively using the app.
            // This handles the case where a student hasn't joined a group yet and has no
            // group user record, which would otherwise show them as disconnected.
            const isConnected = student.id === this.user.id
                || ((_b = (_a = studentGroup === null || studentGroup === void 0 ? void 0 : studentGroup.getUserById(student.id)) === null || _a === void 0 ? void 0 : _a.connected) !== null && _b !== void 0 ? _b : false);
            return {
                id: student.id,
                isConnected,
                name: formatStudentName(student.fullName, this.showLastNameFirst)
            };
        });
    }
    canDragStudent(studentId) {
        if (this.isTeacherMode) {
            return true; // Teachers and researchers can drag any student
        }
        return studentId === this.user.id; // Students can only drag themselves
    }
    isDropTarget(groupId) {
        const studentBeingMoved = this.draggingStudentId || this.effectiveSelectedStudentId;
        return studentBeingMoved !== null && this.studentGroupMap.get(studentBeingMoved) !== groupId;
    }
    isNoGroupDropTarget() {
        const studentBeingMoved = this.draggingStudentId || this.effectiveSelectedStudentId;
        return this.isTeacherMode
            && studentBeingMoved !== null
            && this.studentGroupMap.get(studentBeingMoved) !== null;
    }
}
//# sourceMappingURL=data:application/json;base64,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